import { useState } from 'react';
import type { RadioProgram } from '@/mocks/adminSchedule';
import { dayNames, dayShortNames, programTypeColors, programTypeLabels } from '@/mocks/adminSchedule';
import ProgramCard from './ProgramCard';

interface WeekViewProps {
  programs: RadioProgram[];
  onEdit?: (program: RadioProgram) => void;
  onDelete?: (program: RadioProgram) => void;
  onDuplicate?: (program: RadioProgram) => void;
  onQuickTimeChange?: (program: RadioProgram) => void;
  onToggleActive?: (program: RadioProgram) => void;
  onCancel?: (program: RadioProgram) => void;
}

const hours: string[] = [];
for (let h = 0; h < 24; h++) {
  hours.push(h.toString().padStart(2, '0') + ':00');
}

export default function WeekView({ programs, onEdit, onDelete, onDuplicate, onQuickTimeChange, onToggleActive, onCancel }: WeekViewProps) {
  const [selectedProgram, setSelectedProgram] = useState<RadioProgram | null>(null);

  const getProgramsForSlot = (dayOfWeek: number, hour: number): RadioProgram[] => {
    return programs.filter((p) => {
      if (p.dayOfWeek !== dayOfWeek) return false;
      const startH = parseInt(p.startTime.split(':')[0]);
      const endH = parseInt(p.endTime.split(':')[0]);
      return hour >= startH && hour < endH;
    });
  };

  const handleProgramClick = (program: RadioProgram) => {
    setSelectedProgram(selectedProgram?.id === program.id ? null : program);
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[900px]">
        {/* Header row */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] gap-1 mb-2">
          <div></div>
          {[0, 1, 2, 3, 4, 5, 6].map((d) => (
            <div key={d} className="text-center pb-2">
              <p className="text-foreground-500 text-[10px] font-semibold">{dayShortNames[d]}</p>
              <p className="text-foreground-400 text-[9px]">{dayNames[d]}</p>
            </div>
          ))}
        </div>

        {/* Time grid */}
        <div className="space-y-0.5">
          {hours.map((hour) => {
            const h = parseInt(hour.split(':')[0]);
            return (
              <div key={hour} className="grid grid-cols-[60px_repeat(7,1fr)] gap-1 group">
                <div className="text-right pr-2 pt-1">
                  <span className="text-foreground-400 text-[10px] font-mono font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                    {hour}
                  </span>
                </div>
                {[0, 1, 2, 3, 4, 5, 6].map((d) => {
                  const slotPrograms = getProgramsForSlot(d, h);
                  const hasPrograms = slotPrograms.length > 0;

                  return (
                    <div
                      key={d}
                      className={`h-9 rounded transition-colors ${
                        hasPrograms
                          ? 'bg-primary-50 border border-primary-200/60 cursor-pointer hover:bg-primary-100'
                          : 'bg-background-100/50 border border-background-200/40'
                      }`}
                      onClick={() => hasPrograms && slotPrograms[0] && handleProgramClick(slotPrograms[0])}
                      title={hasPrograms ? slotPrograms.map((p) => p.name).join(', ') : ''}
                    >
                      {hasPrograms && (
                        <div className="px-1.5 py-0.5 h-full flex items-center">
                          <span className="text-foreground-800 text-[9px] font-semibold truncate leading-tight">
                            {slotPrograms[0].name}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Program detail tooltip */}
        {selectedProgram && (
          <div className="mt-4 p-4 rounded-xl bg-background-50 border border-background-200/70">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-background-200 overflow-hidden">
                  <img src={selectedProgram.hostAvatar} alt="" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-foreground-950 font-semibold text-sm">{selectedProgram.name}</p>
                  <p className="text-foreground-500 text-xs">
                    {selectedProgram.startTime} - {selectedProgram.endTime} · {selectedProgram.host}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold ${programTypeColors[selectedProgram.type].bg} ${programTypeColors[selectedProgram.type].text}`}>
                  {programTypeLabels[selectedProgram.type]}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {onToggleActive && (
                  <button onClick={() => onToggleActive(selectedProgram)} className={`w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
                    selectedProgram.isActive ? 'bg-emerald-50 hover:bg-emerald-100' : 'bg-foreground-100 hover:bg-foreground-200'
                  }`} title={selectedProgram.isActive ? 'Desactivar' : 'Activar'}>
                    <i className={`${selectedProgram.isActive ? 'ri-toggle-line text-emerald-600' : 'ri-toggle-fill text-foreground-400'} text-xs`}></i>
                  </button>
                )}
                {onQuickTimeChange && (
                  <button onClick={() => onQuickTimeChange(selectedProgram)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-sky-50" title="Cambiar horario">
                    <i className="ri-time-line text-foreground-500 text-xs"></i>
                  </button>
                )}
                {onCancel && !selectedProgram.cancelled && (
                  <button onClick={() => onCancel(selectedProgram)} className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100" title="Cancelar programa">
                    <i className="ri-close-circle-line text-red-500 text-xs"></i>
                  </button>
                )}
                {onDuplicate && (
                  <button onClick={() => onDuplicate(selectedProgram)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-primary-50" title="Duplicar">
                    <i className="ri-file-copy-line text-foreground-500 text-xs"></i>
                  </button>
                )}
                {onEdit && (
                  <button onClick={() => onEdit(selectedProgram)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
                    <i className="ri-pencil-line text-foreground-500 text-xs"></i>
                  </button>
                )}
                {onDelete && (
                  <button onClick={() => onDelete(selectedProgram)} className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100">
                    <i className="ri-delete-bin-line text-red-500 text-xs"></i>
                  </button>
                )}
                <button onClick={() => setSelectedProgram(null)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
                  <i className="ri-close-line text-foreground-500 text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}